import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { loadScript, removeScript } from "../../utils/helpers";
import { TRUSTPILOT_SCRIPT } from "../../utils/general-config";
import StarIcon from "../../assets/feedback/star.svg";

const TrustpilotModal = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const widgetRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;

    const loadWidget = () => {
      if (window.Trustpilot && widgetRef.current) {
        window.Trustpilot.loadFromElement(widgetRef.current, true);
      }
    };

    loadScript(TRUSTPILOT_SCRIPT);

    const script = document.querySelector(`script[src="${TRUSTPILOT_SCRIPT}"]`);

    if (window.Trustpilot) {
      loadWidget();
    } else if (script) {
      script.addEventListener("load", loadWidget, { once: true });
    }

    return () => {
      removeScript(TRUSTPILOT_SCRIPT);
    };
  }, [isOpen]);

  const handleRateClick = () => {
    window.open(
      process.env.REACT_APP_TRUSTPILOT_URL,
      "_blank",
      "noopener,noreferrer",
    );
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-5 animate-[fadeIn_0.3s_ease-out]"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-[#2B2420]/45" />

      <div
        className="relative w-full max-w-[420px] bg-white rounded-3xl p-6 sm:p-8 shadow-[0_30px_70px_-20px_rgba(43,36,32,0.55)] text-center animate-[slideUp_0.4s_ease-out]"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute top-3.5 right-3.5 w-8 h-8 border-none rounded-full bg-[#2B2420]/[0.06] cursor-pointer text-[#6E635B] text-base flex items-center justify-center"
        >
          ✕
        </button>

        <div className="text-2xl">🎉</div>

        <h3 className="font-extrabold tracking-tight text-[clamp(1.2rem,3vw,1.45rem)] mt-2.5">
          {t("component.trustpilot_modal.title")}
        </h3>

        <p className="text-[#6E635B] text-[0.95rem] mt-2.5">
          {t("component.trustpilot_modal.description")}
        </p>

        <div className="flex gap-1 justify-center mt-5">
          {[...Array(5)].map((_, i) => (
            <img
              key={i}
              src={StarIcon}
              alt="Star"
              className="w-[30px] h-[30px]"
            />
          ))}
        </div>

        <button
          type="button"
          onClick={handleRateClick}
          className="block w-full mt-5 py-4 border-none rounded-[14px] bg-[#00B67A] text-white font-bold text-base text-center cursor-pointer"
        >
          {t("component.trustpilot_modal.rate_cta")}
        </button>

        <button
          type="button"
          onClick={onClose}
          className="mt-3 border-none bg-transparent text-[#8A7F76] font-bold text-[0.88rem] cursor-pointer"
        >
          {t("component.trustpilot_modal.maybe_later")}
        </button>

        {/* Hidden Trustpilot widget kept mounted for tracking/verification */}
        <div
          ref={widgetRef}
          className="trustpilot-widget hidden"
          data-locale="en-US"
          data-template-id="56278e9abfbbba0bdcd568bc"
          data-businessunit-id="689db4768ab85d60204b7295"
          data-style-height="52px"
          data-style-width="100%"
          data-token="3b6d6b8b-2b76-47f4-8a84-9b244a3cf9a9"
        >
          <a
            href={process.env.REACT_APP_TRUSTPILOT_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            Trustpilot
          </a>
        </div>
      </div>
    </div>
  );
};

export default TrustpilotModal;
